import React from 'react';
import { Badge } from '../ui/badge';
import {
  Clock, Eye, AlertCircle, CheckCircle, XCircle, Ban,
  FileSignature, Lock, DollarSign, Award, ThumbsUp, ThumbsDown, Sparkles
} from 'lucide-react';

const STATUS_CONFIG = {
  incomplete: { color: "bg-amber-100 text-amber-800 border-amber-300", icon: AlertCircle, label: "Incomplete" },
  pending_info_docs: { color: "bg-amber-50 text-amber-900 border-amber-400 font-semibold", icon: AlertCircle, label: "Application Started — Pending Info & Docs" },
  submitted_from_link: { color: "bg-cyan-50 text-cyan-800 border-cyan-300 font-semibold", icon: Sparkles, label: "Submitted from Link" },
  submitted: { color: "bg-blue-100 text-blue-800 border-blue-200", icon: Clock, label: "Submitted" },
  submission_complete: { color: "bg-sky-100 text-sky-800 border-sky-200", icon: CheckCircle, label: "Submission Complete" },
  prequalified: { color: "bg-indigo-100 text-indigo-800 border-indigo-200", icon: Award, label: "Prequalified" },
  borrower_accepts_offer: { color: "bg-lime-100 text-lime-800 border-lime-200", icon: ThumbsUp, label: "Borrower Accepts" },
  borrower_declines_offer: { color: "bg-red-600 text-white border-red-700", icon: ThumbsDown, label: "Borrower Declines" },
  under_review: { color: "bg-amber-100 text-amber-800 border-amber-200", icon: Eye, label: "Under Review" },
  pending_docs: { color: "bg-orange-100 text-orange-800 border-orange-200", icon: AlertCircle, label: "Pending Docs/Info" },
  approved: { color: "bg-emerald-100 text-emerald-800 border-emerald-200", icon: CheckCircle, label: "Approved" },
  declined: { color: "bg-red-600 text-white border-red-700", icon: XCircle, label: "Declined" },
  withdrawn: { color: "bg-gray-100 text-gray-700 border-gray-300", icon: Ban, label: "Withdrawn" },
  closing: { color: "bg-violet-100 text-violet-800 border-violet-200", icon: FileSignature, label: "In Closing" },
  closed: { color: "bg-slate-200 text-slate-700 border-slate-300", icon: Lock, label: "Closed" },
  funded: { color: "bg-teal-100 text-teal-800 border-teal-200", icon: DollarSign, label: "Funded" },
  broker_attention_needed: { color: "bg-white text-red-700 border-white font-bold", icon: AlertCircle, label: "Broker Attention Needed" },
  file_ready_for_underwriter: { color: "bg-purple-100 text-purple-800 border-purple-300 font-bold", icon: CheckCircle, label: "File Ready for Underwriter" },
  underwriter_attention_needed: { color: "bg-white text-purple-700 border-white font-bold", icon: AlertCircle, label: "Underwriter Attention Needed" },
  in_document_review: { color: "bg-amber-100 text-amber-800 border-amber-300", icon: Eye, label: "In Document Review" },
  in_queue_for_underwriting: { color: "bg-yellow-100 text-yellow-800 border-yellow-300", icon: Clock, label: "In Queue for Underwriting" },
  in_initial_underwriting: { color: "bg-teal-100 text-teal-800 border-teal-300", icon: Eye, label: "In Initial Underwriting" },
  in_final_underwriting: { color: "bg-emerald-100 text-emerald-800 border-emerald-300", icon: Eye, label: "In Final Underwriting" },
  // Merchant Processing-specific
  mp_quote_issued:               { color: "bg-cyan-100 text-cyan-800 border-cyan-300 font-bold", icon: FileSignature, label: "Quote Issued" },
  mp_merchant_accepts_quote:     { color: "bg-lime-100 text-lime-800 border-lime-300 font-bold", icon: ThumbsUp, label: "Merchant Accepts Quote" },
  mp_please_collect_digital_mpa: { color: "bg-orange-100 text-orange-800 border-orange-300 font-bold", icon: AlertCircle, label: "Please Collect Digital MPA" },
  mp_ready_for_onboarding:       { color: "bg-violet-100 text-violet-800 border-violet-300 font-bold", icon: CheckCircle, label: "Ready for Onboarding" },
  mp_onboarding:                 { color: "bg-indigo-100 text-indigo-800 border-indigo-300 font-bold", icon: Eye, label: "Onboarding" },
  mp_activated:                  { color: "bg-emerald-100 text-emerald-900 border-emerald-400 font-bold", icon: CheckCircle, label: "Activated" },
  mp_account_approved:           { color: "bg-emerald-100 text-emerald-800 border-emerald-300 font-bold", icon: CheckCircle, label: "Account Approved" },
  mp_account_declined:           { color: "bg-red-600 text-white border-red-700 font-bold", icon: XCircle, label: "Account Declined" },
};

export const StatusBadge = ({ status, productType }) => {
  let config = STATUS_CONFIG[status] || STATUS_CONFIG.submitted;
  // For merchant processing, "submitted" displays as "Submitted for Quote"
  if (productType === 'merchant_processing' && status === 'submitted') {
    config = { ...config, label: 'Submitted for Quote' };
  }
  const IconComponent = config.icon;

  return (
    <Badge className={`${config.color} border flex items-center gap-1 font-medium`} data-testid={`status-badge-${status}`}>
      <IconComponent className="h-3 w-3" />
      {config.label}
    </Badge>
  );
};

export const ALL_STATUSES = Object.entries(STATUS_CONFIG).map(([value, cfg]) => ({
  value,
  label: cfg.label,
}));

// Subtle row highlight colors keyed by status — meant for light backgrounds
const STATUS_ROW_BG = {
  incomplete:              'bg-amber-100 border-l-[5px] border-l-amber-500',
  pending_info_docs:       'bg-amber-50 border-l-[5px] border-l-amber-400',
  submitted:               'bg-blue-100/80 border-l-[5px] border-l-blue-400',
  submission_complete:     'bg-sky-100/80 border-l-[5px] border-l-sky-500',
  prequalified:            'bg-indigo-100/80 border-l-[5px] border-l-indigo-500',
  borrower_accepts_offer:  'bg-lime-100/80 border-l-[5px] border-l-lime-500',
  borrower_declines_offer: 'bg-slate-800 border-l-[5px] border-l-slate-950 text-white [&_*]:text-slate-100 [&_.text-slate-500]:text-slate-400 [&_.text-slate-600]:text-slate-300 [&_.text-xs]:text-slate-400',
  under_review:            'bg-yellow-100/80 border-l-[5px] border-l-yellow-500',
  pending_docs:            'bg-orange-100/80 border-l-[5px] border-l-orange-500',
  approved:                'bg-emerald-100/80 border-l-[5px] border-l-emerald-500',
  declined:                'bg-slate-800 border-l-[5px] border-l-slate-950 text-white [&_*]:text-slate-100 [&_.text-slate-500]:text-slate-400 [&_.text-slate-600]:text-slate-300 [&_.text-xs]:text-slate-400',
  withdrawn:               'bg-slate-700 border-l-[5px] border-l-slate-900 text-white [&_*]:text-slate-100 [&_.text-slate-500]:text-slate-400 [&_.text-slate-600]:text-slate-300 [&_.text-xs]:text-slate-400',
  closing:                 'bg-violet-100/80 border-l-[5px] border-l-violet-500',
  closed:                  'bg-slate-200/80 border-l-[5px] border-l-slate-500',
  funded:                  'bg-teal-100/80 border-l-[5px] border-l-teal-500',
  broker_attention_needed:      'bg-red-600 border-l-[5px] border-l-red-900',
  file_ready_for_underwriter:   'bg-purple-400 border-l-[5px] border-l-purple-700',
  underwriter_attention_needed: 'bg-purple-600 border-l-[5px] border-l-purple-900',
  in_document_review:          'bg-amber-100/80 border-l-[5px] border-l-amber-500',
  in_queue_for_underwriting:   'bg-yellow-100/80 border-l-[5px] border-l-yellow-500',
  in_initial_underwriting:     'bg-teal-100/80 border-l-[5px] border-l-teal-500',
  in_final_underwriting:       'bg-emerald-100/80 border-l-[5px] border-l-emerald-500',
  // Merchant Processing
  mp_quote_issued:               'bg-cyan-100/80 border-l-[5px] border-l-cyan-500',
  mp_merchant_accepts_quote:     'bg-lime-100/80 border-l-[5px] border-l-lime-500',
  mp_please_collect_digital_mpa: 'bg-orange-100/80 border-l-[5px] border-l-orange-500',
  mp_ready_for_onboarding:       'bg-violet-100/80 border-l-[5px] border-l-violet-500',
  mp_onboarding:                 'bg-indigo-100/80 border-l-[5px] border-l-indigo-500',
  mp_activated:                  'bg-emerald-200/80 border-l-[5px] border-l-emerald-600',
  mp_account_approved:           'bg-emerald-100/80 border-l-[5px] border-l-emerald-500',
  mp_account_declined:           'bg-slate-800 border-l-[5px] border-l-slate-950 text-white [&_*]:text-slate-100 [&_.text-slate-500]:text-slate-400 [&_.text-slate-600]:text-slate-300 [&_.text-xs]:text-slate-400',
};

// Statuses that should pulse to catch attention
const ATTENTION_STATUSES = new Set([
  'broker_attention_needed',
  'file_ready_for_underwriter',
  'underwriter_attention_needed',
]);

/** Returns subtle row highlight className for the given status */
export const getStatusRowBg = (status) => STATUS_ROW_BG[status] || '';

/** Returns true if this status row should have a pulse animation */
export const isAttentionStatus = (status) => ATTENTION_STATUSES.has(status);

export default StatusBadge;
